import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Breadcrumb } from "react-bootstrap";
import { TIMESHEET } from "src/constants/route-paths";
import FileUplaoder from "src/components/file-uploader";
import { allowedExcelTypes } from "src/util/validators/file";
import * as XLSX from "xlsx";
import idx from "idx";
import { getGlobalSetting } from "src/actions/global-setting.action";
import CustomTable from "src/components/table/table";
import { importTimesheet } from "src/actions/timesheet.action";
import { getUserList } from "src/actions/user-management.action";
import moment from 'moment';
import { enqueueSnackbar } from "src/actions/common.action";
import ExcelJS from 'exceljs';

const timesheetFields = {
    resource: {
        key: 'Resource',
        label: 'Resource',
        default: '',
    },
    project: {
        key: 'Project',
        label: 'Project',
        default: '',
    },
    projectTask: {
        key: 'Project Task',
        label: 'Project Task',
        default: '',
    },
};

function ImportTimesheet({ globalUser }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [status, setStatus] = useState({
        total: 0,
        valid: 0,
        invalid: 0
    });
    const [, setMappers] = useState({});
    const [globalSettings, setGlobalSettings] = useState({
        startTime: null,
        endTime: null,
        workingDays: {},
        sundayStartsWeek: false
    });
    const [showInvalid, setShowInvalid] = useState(false);
    const [users, setUsers] = useState([]);
    const [userMap, setUserMap] = useState({});
    const userDayTimeTracker = {};

    useEffect(() => {
        // Fetch global settings using getGlobalSetting action
        const fetchGlobalSettings = async () => {
            try {
                const response = await dispatch(getGlobalSetting('LETTERHEAD'));
                const letterheadData = idx(response, _ => _.data) || {};

                const rawStart  = idx(letterheadData, _ => _.start_time)  || '00:00';
                const rawFinish = idx(letterheadData, _ => _.finish_time) || '00:00';

                const startTime  = moment.utc(rawStart,  'HH:mm').toDate();
                const finishTime = moment.utc(rawFinish, 'HH:mm').toDate();
                
                // Safely extract workingDays with fallback to empty object
                const workingDays = idx(letterheadData, _ => _.workingDays.days) || {};
                
                setGlobalSettings({
                    startTime,
                    endTime: finishTime,
                    workingDays,
                    sundayStartsWeek: idx(letterheadData, _ => _.sunday_as_new_week) !== '' || false
                });
            } catch (error) {
                console.error('Error fetching global settings:', error);
                // Set default values in case of error
                setGlobalSettings({
                    startTime: null,
                    endTime: null,
                    workingDays: {}
                });
            }
        };
        
        fetchGlobalSettings();
        fetchUsers();
    }, [dispatch]);
    
    const fetchUsers = async () => {
        try {
            const response = await dispatch(getUserList());
            if (response && Array.isArray(response)) {
                setUsers(response);
                
                // Create a map of user names to IDs for easy lookup
                const userNameMap = {};
                response.forEach(user => {
                    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase().trim();
                    userNameMap[fullName] = {id:user.id, hourlyRate: user.hourlyRate};
                });
                setUserMap(userNameMap);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const validateResponse = (response) => {
        if (!response || !Array.isArray(response.errors)) {
            console.error('Invalid response format:', response);
            return;
        }
        // map rowId -> error
        const errorMap = {};
        response.errors.forEach(err => {
            const rid = err.entry?.rowId;
            if (rid !== undefined) {
                errorMap[rid] = typeof err.error === 'string' ? err.error : (err.error?.message || 'Unknown error');
            }
        });
        const updated = data.map(item => {
            if (errorMap[item.rowId]) {
                return { ...item, importError: errorMap[item.rowId], valid: false, rowStyle: { backgroundColor: '#fff3cd' } };
            }
            return item;
        });
        const count = { total: updated.length, valid: updated.filter(i => i.valid).length, invalid: updated.filter(i => !i.valid).length };
        setData(updated);
        setStatus(count);
        if (count.invalid > 0) {
            dispatch(enqueueSnackbar({
                message: `${count.invalid} ${count.invalid === 1 ? 'entry has' : 'entries have'} import errors. Please review the highlighted rows.`,
                options: { key: Date.now(), variant: 'warning', autoHideDuration: 5000 }
            }));
            setShowInvalid(true);
        }
    };

    // Function to find user ID from resource name that might have titles or extra text
    const findUserId = (resourceName) => {
        if (!resourceName) return null;
        
        const resourceStr = resourceName.toString().toLowerCase().trim();
        
        // 1. Try exact match first
        if (userMap[resourceStr]) return userMap[resourceStr];
        
        // 2. Try to extract the name from the resource string
        // Split by common separators and check each part
        const parts = resourceStr.split(/[-,();:]+/).map(part => part.trim()).filter(Boolean);
        for (const part of parts) {
            if (userMap[part]) return userMap[part];
        }
        
        // 3. Try to find if any user's name is contained within the resource string
        // This helps with cases like "John Smith (Manager)" or "Dr. Jane Doe"
        for (const user of users) {
            const firstName = user.firstName ? user.firstName.toLowerCase() : '';
            const lastName = user.lastName ? user.lastName.toLowerCase() : '';
            
            // Look for first and last name together in the resource string
            if (firstName && lastName && resourceStr.includes(`${firstName} ${lastName}`)) {
                return {id:user.id, hourlyRate: user.hourlyRate};
            }
            
            // For cases where only one name part appears
            if (firstName && resourceStr.includes(firstName) && !lastName) {
                return {id:user.id, hourlyRate: user.hourlyRate};
            }
            if (lastName && resourceStr.includes(lastName) && !firstName) {
                return {id:user.id, hourlyRate: user.hourlyRate};
            }
        }
        
        return null;
    };

    const cancelHandler = () => {
        navigate(`/${TIMESHEET}`);
    };

    const getTimesheetColumns = () => {
        return [
            { title: 'Resource', field: 'resource' },
            { title: 'Project', field: 'project' },
            { title: 'Project Task', field: 'projectTask' },
            { 
                title: 'Date', 
                field: 'date',
                render: rowData => {
                    // Don't show date for invalid/skipped records
                    if (!rowData.valid && rowData.error && rowData.error.length > 0) {
                        return '-';
                    }
                    return moment(rowData.date).format('DD/MM/YYYY');
                }
            },
            {
                title: 'Start Time',
                field: 'start_time',
                render: rowData => {
                    // Don't show start time for invalid/skipped records
                    if (!rowData.valid && rowData.error && rowData.error.length > 0) {
                        return '-';
                    }
                    return moment.utc(rowData.start_time).format('h:mm a');
                }
            },
            {
                title: 'End Time',
                field: 'end_time',
                render: rowData => {
                    // Don't show end time for invalid/skipped records
                    if (!rowData.valid && rowData.error && rowData.error.length > 0) {
                        return '-';
                    }
                    return moment.utc(rowData.end_time).format('h:mm a');
                }
            },
            {
                title: 'Normal Hours',
                field: 'normalHours',
                type: 'numeric',
                render: rowData => {
                    return rowData.normalHours;
                }
            },
            {
                title: 'Valid',
                field: 'valid',
                filtering: false,
                class: 'text-right',
                width: '250px',
                render: (data) => {
                    if (data.importError) {
                        return (
                            <p className="text-warning">{data.importError}</p>
                        );
                    }
                    
                    if (!data.valid && data.error && data.error.length > 0) {
                        // If there are multiple errors, show them as a list
                        if (data.error.length > 1) {
                            return (
                                <div className="text-danger">
                                    <strong>Multiple issues:</strong>
                                    <ul className="mb-0 ps-3 mt-1">
                                        {data.error.map((err, index) => (
                                            <li key={index}>{err.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        }
                        // If just one error, show it directly
                        return (
                            <p className="text-danger mb-0">{data.error[0]?.message}</p>
                        );
                    }
                    
                    return data.valid ? (
                        <span className="text-success">✓</span>
                    ) : (
                        <p className="text-danger mb-0">Invalid</p>
                    );
                },
            }
        ];
    };


    const resources = [
        { id: 1, name: 'Employee 1' },
        { id: 2, name: 'Employee 2' },
        { id: 3, name: 'Employee 3' },
        { id: 4, name: 'Employee 4' },
        { id: 5, name: 'Employee 5' },
        { id: 6, name: 'Employee 6' },
    ];

    const projectsAndTasks = {
        Booval: ['Floor1', 'Floor2', 'Floor3', 'Floor4'],
        Ducale: ['Basement', 'Main', 'Floor1', 'Floor2'],
        Bogo: ['Basement', 'Main', 'Floor1', 'Floor2'],
        WidenMere: ['Basement', 'Main', 'Floor1', 'Floor2'],
    };

     // Convert column number to Excel column letter (e.g., 0 -> A, 1 -> B, etc.)
     const colNumberToLetter = (colNum) => {
        let letter = '';
        while (colNum >= 0) {
            letter = String.fromCharCode((colNum % 26) + 65) + letter;
            colNum = Math.floor(colNum / 26) - 1;
        }
        return letter;
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Main');
        const employeeListSheet = workbook.addWorksheet('Employee List');
        const projectCostSheet = workbook.addWorksheet('Project Task');

        // Main Sheet Headers (Only define columns A to D initially)
        worksheet.columns = [
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: 'Input Date Below', key: 'inputDate', width: 15 },
        ];
        worksheet.getRow(1).font = { bold: true };

        // Add "Timesheet" header in merged cells from column I to K
        worksheet.mergeCells('I1:K1');
        worksheet.getCell('I1').value = 'Timesheet';
        worksheet.getCell('I1').font = { bold: true };
        worksheet.getCell('I1').alignment = { horizontal: 'center' };

        // Add "Resource", "Project", and "Cost Centre" headers in Row 2
        worksheet.getCell('A2').value = 'Resource';
        worksheet.getCell('B2').value = 'Project';
        worksheet.getCell('C2').value = 'Project Task';
        worksheet.getRow(2).font = { bold: true };

        worksheet.views = [
            {state: 'frozen', xSplit: 3, ySplit: 3, topLeftCell: 'D4', activeCell: 'D4'}
        ]

        // Add Date Headers in Row 2 and "Hours" sub-header in Row 3 — one column per date
        const dateHeaders = generateDateHeaders();
        for (let i = 0; i < dateHeaders.length; i++) {
            const dateColStart = 3 + i;
            const dateCell = `${colNumberToLetter(dateColStart)}2`;
            worksheet.getCell(dateCell).value = dateHeaders[i];
            worksheet.getCell(dateCell).font = { bold: true };
            worksheet.getCell(dateCell).alignment = { horizontal: 'center' };
            worksheet.getCell(`${colNumberToLetter(dateColStart)}3`).value = 'Hours';
        }
        worksheet.getRow(3).font = { bold: true };

        // Ensure Row 4 is empty (for data entry)
        worksheet.getRow(4).values = [];

        // Set blue background color for the first 3 columns
        // Header cells (rows 1-3) get a deeper blue
        const deepBlue = 'FF99D6FF'; // Darker shade for headers
        const lightBlue = 'FFCCECFF'; // Lighter shade for data cells
        
        // Apply light blue fill to all cells in the first 3 columns
        worksheet.getColumn(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: lightBlue }
        };
        
        worksheet.getColumn(2).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: lightBlue }
        };
        
        worksheet.getColumn(3).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: lightBlue }
        };
        
        // Apply borders to data cells only (rows 4+)
        for (let row = 4; row <= 100; row++) {
            for (let col = 1; col <= 3; col++) {
                worksheet.getCell(row, col).border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } }
                };
            }
        }
        
        // Set header cells to deep blue (no borders)
        worksheet.getCell('A1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('B1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('C1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('A2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('B2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('C2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('A3').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('B3').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };
        worksheet.getCell('C3').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: deepBlue }
        };

        // Employee List Sheet
        employeeListSheet.columns = [{ header: 'Resources', key: 'name', width: 20 }];
        resources.forEach((r) => employeeListSheet.addRow([r.name]));

        // Project and Cost Centre Sheet
        projectCostSheet.columns = [
            { header: 'Project 1', key: 'project1', width: 20 },
            { header: 'Project 2', key: 'project2', width: 20 },
            { header: 'Project 3', key: 'project3', width: 20 },
            { header: 'Project 4', key: 'project4', width: 20 },
        ];
        const maxTasks = Math.max(...Object.values(projectsAndTasks).map((tasks) => tasks.length));
        for (let i = 0; i < maxTasks; i++) {
            const row = [];
            Object.values(projectsAndTasks).forEach((tasks) => {
                row.push(tasks[i] || '');
            });
            projectCostSheet.addRow(row);
        }

        // Apply Data Validation for Dropdowns to All Cells in Columns A, B, and C (Rows 4 to 100)
        for (let row = 4; row <= 100; row++) {
            // Resource Dropdown (Column A)
            const resourceCell = worksheet.getCell(`${colNumberToLetter(0)}${row}`);
            resourceCell.dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [`'Employee List'!$A$2:$A$${resources.length + 1}`],
            };

            // Project Dropdown (Column B)
            const projectCell = worksheet.getCell(`${colNumberToLetter(1)}${row}`);
            projectCell.dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [`'Project Task'!$A$1:$D$1`],
            };

            // Cost Centre Dropdown (Column C) - Dynamic based on Project
            const costCentreCell = worksheet.getCell(`${colNumberToLetter(2)}${row}`);
            costCentreCell.dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [
                    `INDIRECT("'Project Task'!$" & CHAR(64 + MATCH(B${row}, 'Project Task'!$A$1:$D$1, 0)) & "$2:" & CHAR(64 + MATCH(B${row}, 'Project Task'!$A$1:$D$1, 0)) & "$" & ${maxTasks + 1})`,
                ],
            };
        }

        // Unlock hour entry cells — one column per date starting from column D (index 3)
        for (let i = 3; i < 3 + dateHeaders.length; i++) {
            for (let j = 4; j <= 100; j++) {
                worksheet.getCell(`${colNumberToLetter(i)}${j}`).protection = { locked: false };
            }
        }

        // Write to File
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Timesheet Template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const generateDateHeaders = () => {
        const headers = [];
        
        try {
            const today = moment();
            
            const workingDays = globalSettings.workingDays || {};
            
            // Convert day name keys to day number map for easier usage
            const dayNameToNumber = {
                'sunday': 0,
                'monday': 1,
                'tuesday': 2,
                'wednesday': 3,
                'thursday': 4,
                'friday': 5,
                'saturday': 6
            };
            
            // Map of day numbers to boolean values
            const activeDayNumbers = {};
            for (const [dayName, isActive] of Object.entries(workingDays)) {
                const dayNumber = dayNameToNumber[dayName.toLowerCase()];
                if (dayNumber !== undefined) {
                    activeDayNumbers[dayNumber] = isActive;
                }
            }
            
            // If no working days are specified or none are active, default to standard work week (Mon-Fri)
            const hasActiveWorkingDays = Object.values(activeDayNumbers).some(active => active === true);
            const defaultWorkDays = { 1: true, 2: true, 3: true, 4: true, 5: true };
            const workingDayMap = hasActiveWorkingDays ? activeDayNumbers : defaultWorkDays;
            
            // Get start of week based on sundayStartsWeek setting
            const firstDayOfWeek = globalSettings.sundayStartsWeek ? 0 : 1;
            const startOfWeek = moment(today).startOf('week');
            
            // If week starts on Monday and sundayStartsWeek is false, add 1 day to startOfWeek
            if (firstDayOfWeek === 1 && !globalSettings.sundayStartsWeek) {
                startOfWeek.add(1, 'day');
            }
            
            // Check each day of the week and include only those specified as working days
            for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
                const currentDate = moment(startOfWeek).add(dayOffset, 'days');
                const dayOfWeek = currentDate.day();
                
                // Include this day if it's marked as a working day in global settings
                if (workingDayMap[dayOfWeek]) {
                    // Format date with day of week, day, month and year
                    const dateString = currentDate.format('dddd, D MMMM YYYY');
                    
                    headers.push(dateString);
                }
            }

            // If no working days were found, fall back to standard work week (Mon-Fri)
            if (headers.length === 0) {
                console.warn('No working days found in settings. Using standard work week (Mon-Fri).');

                for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
                    const currentDate = moment(startOfWeek).add(dayOffset, 'days');
                    const dayOfWeek = currentDate.day();

                    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                        headers.push(currentDate.format('dddd, D MMMM YYYY'));
                    }
                }
            }
        } catch (error) {
            console.error('Error generating date headers:', error);

            const startOfWeek = moment().startOf('week').add(1, 'day');
            for (let i = 0; i < 5; i++) {
                headers.push(moment(startOfWeek).add(i, 'days').format('dddd, D MMMM YYYY'));
            }
        }
        
        return headers;
    };

    const parseXlsx = (file) => {
        setData([])
        setMappers([])
        setShowInvalid(false)
        const reader = new FileReader()
        reader.onload = (event) => {
            const workbook = XLSX.read(event.target.result, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            
            // Get the raw sheet data with specific ranges
            const range = XLSX.utils.decode_range(sheet['!ref']);
            const allRows = [];
            
            // Find the last non-empty row to avoid processing hundreds of empty rows
            let lastNonEmptyRow = 0;
            for (let r = range.s.r; r <= range.e.r; r++) {
                let rowHasData = false;
                for (let c = range.s.c; c <= range.e.c; c++) {
                    const cellAddress = XLSX.utils.encode_cell({ r, c });
                    const cell = sheet[cellAddress];
                    if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
                        rowHasData = true;
                        lastNonEmptyRow = r;
                        break;
                    }
                }
                if (!rowHasData && r > 10) {  // Allow for a few empty rows in case of formatting
                    // If we've gone 10 rows without data after row 10, assume we're at the end
                    if (r - lastNonEmptyRow > 10) {
                        break;
                    }
                }
            }
            
            // Set a reasonable upper limit to prevent excessive processing
            const upperRowLimit = Math.min(lastNonEmptyRow + 5, range.e.r);
            
            // Extract all cells including merged cells, but only up to the reasonably determined last row
            for (let r = range.s.r; r <= upperRowLimit; r++) {
                const rowData = [];
                for (let c = range.s.c; c <= range.e.c; c++) {
                    const cellAddress = XLSX.utils.encode_cell({ r, c });
                    const cell = sheet[cellAddress];
                    rowData.push(cell ? cell.v : undefined);
                }
                allRows.push(rowData);
            }
            
            
            // First row has headers "Resource", "Project", "Cost Centre" in A-C
            // and day headers in merged cells starting from column D
            const headerRow = allRows[1] || [];
            // Second row has "Hours" sub-header per date (new format) or "Normal|OT1|OT2" (legacy)
            const subHeaderRow = allRows[2] || [];
            
            // Find resource, project, cost centre columns in the header row
            const resourceColIndex = headerRow.findIndex(h => h === timesheetFields.resource.key);
            const projectColIndex = headerRow.findIndex(h => h === timesheetFields.project.key);
            const projectTaskColIndex = headerRow.findIndex(h => h === timesheetFields.projectTask.key);
            
            // Track missing required columns
            const missingColumns = [];
            if (resourceColIndex === -1) missingColumns.push(timesheetFields.resource.key);
            if (projectColIndex === -1) missingColumns.push(timesheetFields.project.key);
            
            // If required columns are missing, notify the user and stop processing
            if (missingColumns.length > 0) {
                dispatch(enqueueSnackbar({
                    message: `Import failed: Missing required column(s): ${missingColumns.join(', ')}`,
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: 'error',
                        autoHideDuration: 5000
                    }
                }));
                return;
            }
            
            // Map to store day headers and their column groups
            const dayColumns = [];
            
            // Data structure to identify day columns and their sub-columns
            // Starting from column D (index 3) which is typically the first day column
            let columnIndex = 3; // Start after Resource, Project, Cost Centre
            
            // Detect date columns — supports new format (Hours) and legacy format (Normal|OT1|OT2)
            while (columnIndex < headerRow.length) {
                const dayHeader = headerRow[columnIndex];

                if (dayHeader !== undefined && (typeof dayHeader === 'string' || typeof dayHeader === 'number')) {
                    const subHeader = subHeaderRow[columnIndex] ? String(subHeaderRow[columnIndex]) : '';
                    const isNewFormat = subHeader.includes('Hours');
                    const isLegacyFormat = subHeader.includes('Normal') &&
                        subHeaderRow[columnIndex + 1] && String(subHeaderRow[columnIndex + 1]).includes('OT1') &&
                        subHeaderRow[columnIndex + 2] && String(subHeaderRow[columnIndex + 2]).includes('OT2');

                    if (isNewFormat || isLegacyFormat) {
                        let displayDate = dayHeader;
                        if (typeof dayHeader === 'number') {
                            const javascriptDate = new Date(1900, 0, dayHeader - (dayHeader > 60 ? 1 : 0));
                            displayDate = javascriptDate.toLocaleDateString(undefined, {
                                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                            });
                        }

                        dayColumns.push({
                            dayHeader: displayDate,
                            excelDate: dayHeader,
                            normalColIndex: columnIndex,
                        });

                        columnIndex += isLegacyFormat ? 3 : 1;
                    } else {
                        columnIndex++;
                    }
                } else {
                    columnIndex++;
                }
            }
            
            
            // Get the actual data rows (skip header and sub-header rows)
            // The template has 3 header rows (rows 0, 1, 2), so start from row 3
            const dataRows = allRows.slice(3);
            
            // Setup mappers based on found columns
            let _mappers = {
                resource: {
                    ...timesheetFields.resource,
                    key: resourceColIndex !== -1 ? resourceColIndex : -1
                },
                project: {
                    ...timesheetFields.project,
                    key: projectColIndex !== -1 ? projectColIndex : -1
                },
                projectTask: {
                    ...timesheetFields.projectTask,
                    key: projectTaskColIndex !== -1 ? projectTaskColIndex : -1
                }
            };  
            
            // Track skipped rows and reasons for better UX
            const skippedRows = [];
            
            // Process data into timesheet entries
            let rowIdCounter = 0;
            let timesheetEntries = [];
            
            // For each data row, create entries for each day that has hours
            dataRows.forEach((row, rowIndex) => {
                // Properly check if row is actually empty (all cells undefined or empty strings)
                const isRowEmpty = !row.length || row.every(cell => cell === undefined || cell === '');
                
                // Skip truly empty rows
                if (isRowEmpty) return;
                
                const resource = row[_mappers.resource.key];
                const project = row[_mappers.project.key];
                const projectTask = _mappers.projectTask.key !== -1 ? row[_mappers.projectTask.key] : '';
                
                // Track skipped rows due to missing mandatory fields
                if (!resource || !project) {
                    const realRowIndex = rowIndex + 4; // +4 because we skipped 3 header rows (0, 1, 2)
                    skippedRows.push({
                        rowNumber: realRowIndex,
                        resource: resource || 'MISSING',
                        project: project || 'MISSING',
                        projectTask: projectTask,
                        
                        reason: `Row ${realRowIndex}: Missing ${!resource ? 'Resource' : ''}${!resource && !project ? ' and ' : ''}${!project ? 'Project' : ''}`
                    });
                    return;
                }
                
                
                
                let hasEntries = false;
                // Process each day column group
                dayColumns.forEach(dayCol => {
                    const normalHoursCell = row[dayCol.normalColIndex];
                    
                    // Validate that hour values are valid numbers
                    const isNormalHoursValid = normalHoursCell === undefined || normalHoursCell === '' || (!isNaN(parseFloat(normalHoursCell)) && isFinite(normalHoursCell));

                    // If normal hours is not a valid number
                    if (!isNormalHoursValid) {
                        const realRowIndex = rowIndex + 4; // +4 because we skipped 3 header rows (0, 1, 2)
                        const invalidField = 'Normal hours';
                        const dateHeader = typeof dayCol.dayHeader === 'string' ? dayCol.dayHeader : 'Unknown date';
                        
                        skippedRows.push({
                            rowNumber: realRowIndex,
                            resource: resource || '',
                            project: project || '',
                            projectTask: projectTask || '',
                            date: new Date(),
                            reason: `Row ${realRowIndex}: Invalid ${invalidField} value for ${dateHeader}`
                        });
                        return; // Skip processing this day column
                    }
                    
                    // Parse normal hours only; OT columns are kept in the template for backward compat but ignored
                    const normalHours = parseFloat(normalHoursCell || 0);

                    // Only create entry if has hours
                    if (normalHours > 0) {
                        hasEntries = true;
                        // Parse the date from the day header - try various formats
                        let dayDate;
                        
                        // Handle both string representations and Excel date serials
                        if (dayCol.excelDate && typeof dayCol.excelDate === 'number') {
                            // Convert Excel date serial to JavaScript Date properly in UTC to avoid timezone issues
                            // Adjust for Excel's leap year bug (set the date in UTC)
                            dayDate = new Date(1900, 0, dayCol.excelDate - (dayCol.excelDate > 60 ? 1 : 0));
                        } else if (typeof dayCol.dayHeader === 'string') {
                            // Try to extract date from formats like "Tuesday, March 18, 2025"
                            const dateMatch = dayCol.dayHeader.match(/([A-Za-z]+),\s+([A-Za-z]+)\s+(\d+),\s+(\d+)/);
                            if (dateMatch) {
                                const [, /* dayOfWeek */, month, day, year] = dateMatch;
                                // Create date with UTC to avoid timezone issues
                                const monthMap = {
                                    'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
                                    'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
                                };
                                dayDate = new Date(parseInt(year), monthMap[month], parseInt(day));
                            } else {
                                // Fallback - try direct parsing with UTC date
                                try {
                                    const parsedDate = new Date(dayCol.dayHeader);
                                    dayDate = new Date(
                                        parsedDate.getFullYear(),
                                        parsedDate.getMonth(),
                                        parsedDate.getDate()
                                    );
                                } catch (e) {
                                    console.error(`Failed to parse date: ${dayCol.dayHeader}`, e);
                                    return;
                                }
                            }
                        }
                        
                        
                        // Skip invalid dates with proper error reporting
                        if (!dayDate || isNaN(dayDate.getTime())) {
                            const realRowIndex = rowIndex + 4; // +4 because we skipped 3 header rows (0, 1, 2)
                            skippedRows.push({
                                rowNumber: realRowIndex,
                                resource: resource || '',
                                project: project || '',
                                projectTask: projectTask || '',
                                reason: `Row ${realRowIndex}: Invalid date format in column "${dayCol.dayHeader}"`
                            });
                            return;
                        }
                        
                        // Create start and end time based on global settings using date-fns
                        let start = new Date(dayDate);
                        let end = new Date(dayDate);
                        
                        try {
                           
                            

                            const { startTime: baseStart, endTime: baseEnd } = globalSettings;

                            const startHours   = baseStart instanceof Date ? baseStart.getUTCHours()   : 8;
                            const startMinutes = baseStart instanceof Date ? baseStart.getUTCMinutes() : 0;
                            const endHours     = baseEnd   instanceof Date ? baseEnd.getUTCHours()     : 17;
                            const endMinutes   = baseEnd   instanceof Date ? baseEnd.getUTCMinutes()   : 0;
                            
                            
                            // Convert day date to appropriate format
                            // Clone the dayDate to avoid mutating it
                            const dayOnly = new Date(dayDate.getTime());
                            
                            // Set start time
                            start = new Date(dayOnly.getTime());
                            const dateKey = `${resource}|${dayDate.toDateString()}`; 



                            if (userDayTimeTracker[dateKey]) {
                                start = new Date(userDayTimeTracker[dateKey]);
                            } else {
                                start = new Date(dayOnly.getTime());
                                start.setUTCHours(startHours, startMinutes, 0, 0);
                            }
                            
                            const totalHours = normalHours;
                            
                            // For end time, calculate based on total hours if provided
                            if (totalHours > 0) {
                                // End time = start time + total hours using moment.js
                                const startMoment = moment(start);
                                const endMoment = startMoment.clone().add(totalHours, 'hours');
                                end = endMoment.toDate();
                                userDayTimeTracker[dateKey] = end;
                            } else {
                                // Use default end time from settings
                                end = new Date(dayOnly.getTime());
                                end.setUTCHours(endHours, endMinutes, 0, 0);
                            }
                        } catch (error) {
                            console.error('Error setting start/end times:', error);
                            // Use default times if there's an error
                            start.setUTCHours(8, 0, 0, 0);
                            end.setUTCHours(17, 0, 0, 0);
                        }

                        const entryLocalDate = moment(dayDate).format('YYYY-MM-DD');
                        
                        timesheetEntries.push({
                            rowId: rowIdCounter++,
                            resource,
                            project,
                            projectTask,
                            date: dayDate,
                            start_time: start,
                            end_time: end,
                            normalHours,
                            entryLocalDate,
                            valid: true
                        });
                    }
                });
                
                // If no entries were created for this row (no hours entered)
                if (!hasEntries) {
                    const realRowIndex = rowIndex + 4; // +4 because we skipped 3 header rows (0, 1, 2)
                    skippedRows.push({
                        rowNumber: realRowIndex,
                        resource,
                        project,
                        reason: `Row ${realRowIndex}: No hours recorded for any day`
                    });
                }
            });
        
            // Notify user about skipped rows if any
            if (skippedRows.length > 0) {
                // Create a summary message
                let summary = '';
                if (skippedRows.length === 1) {
                    const skippedRow = skippedRows[0];
                    summary = `Row ${skippedRow.rowNumber} was skipped: ${skippedRow.reason.split(': ')[1]}`;
                } else {
                    summary = `${skippedRows.length} row(s) were skipped during import`;
                }
                
                // Show notification with summary
                dispatch(enqueueSnackbar({
                    message: summary,
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: 'warning',
                        autoHideDuration: 5000
                    }
                }));
                
                // Add skipped rows to the data with valid=false for display in the table
                const skippedEntries = skippedRows.map(skipped => ({
                    rowId: rowIdCounter++,
                    resource: skipped.resource || 'MISSING',
                    project: skipped.project || 'MISSING',
                    projectTask: skipped.projectTask || '',
                    date: null,
                    start_time: null,
                    end_time: null,
                    normalHours: null,
                    valid: false,
                    error: [{ message: skipped.reason }],
                    rowStyle: { backgroundColor: '#ffebee' }
                }));
                
                timesheetEntries = [...timesheetEntries, ...skippedEntries];
            }
            
            setMappers(_mappers);
            setData(timesheetEntries);
            validateData(timesheetEntries);
        };
        reader.readAsArrayBuffer(file);
    };
    
    const validateData = (timesheetData = data) => {
        const validatedData = timesheetData.map(item => {
            // Skip already marked invalid items (from skipped rows)
            if (item.error && item.error.length > 0) {
                return item;
            }
            
            // Collection of validation errors for this item
            const validationErrors = [];
            
            // Basic validation - ensure resource and project are present
            if (!item.resource) {
                validationErrors.push('Resource is required');
            }
            
            if (!item.project) {
                validationErrors.push('Project is required');
            }
            
            // Validate date exists and is valid
            if (!item.date) {
                validationErrors.push('Date is required');
            } else if (isNaN(new Date(item.date).getTime())) {
                validationErrors.push('Date is invalid');
            }
            
            // Validate hours - check if they are valid numbers and at least one type has hours
            let hasValidHours = false;
            
            // Check if normalHours is a valid number
            if (typeof item.normalHours === 'undefined' || item.normalHours === null) {
                validationErrors.push('Normal hours value is missing');
            } else if (isNaN(parseFloat(item.normalHours)) || !isFinite(item.normalHours)) {
                validationErrors.push('Normal hours must be a valid number');
            } else if (parseFloat(item.normalHours) > 0) {
                hasValidHours = true;
            }

            if (!hasValidHours && !validationErrors.some(err => err.includes('hours'))) {
                validationErrors.push('Normal hours must be greater than 0');
            }
            
            // If there are validation errors, mark the item as invalid
            if (validationErrors.length > 0) {
                return {
                    ...item,
                    valid: false,
                    error: validationErrors.map(message => ({ message })),
                    rowStyle: { backgroundColor: '#ffebee' }
                };
            }
            
            // Look up user ID for this resource name
            const userId = findUserId(item.resource);
            
            // Validate that we found a matching user
            if (!userId) {
                return {
                    ...item,
                    valid: false,
                    error: [{ message: `Could not match "${item.resource}" to any user in the system` }],
                    rowStyle: { backgroundColor: '#ffebee' }
                };
            }
            
            // Add userId to the item data
            item.userId = userId.id;
            item.hourlyRate = userId.hourlyRate;
            
            return {
                ...item,
                valid: true,
                error: null
            };
        });
        
        const count = {
            total: validatedData.length,
            valid: validatedData.filter(item => item.valid).length,
            invalid: validatedData.filter(item => !item.valid).length
        };
        
        setData(validatedData);
        setStatus(count);
        
        return { count, items: validatedData.filter(item => item.valid) };
    };
    
    const importData = async (ignore = false) => {
        const { count, items } = validateData();
        
        if (count.total === 0) {
            // Show message if no data to import
            dispatch(enqueueSnackbar({
                message: 'No data to import. Please upload a valid timesheet file.',
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                    autoHideDuration: 5000
                }
            }));
            return;
        }
        
        if ((count.total === count.valid || (count.valid > 0 && ignore)) && count.total > 0) {
            try {
                // Show loading notification
                dispatch(enqueueSnackbar({
                    message: 'Importing timesheet data, please wait...',
                    options: {
                        key: 'import-loading',
                        variant: 'info',
                        autoHideDuration: 3000
                    }
                }));

                // Format data for API, now with userId instead of resource name
                const formattedItems = items.map(item => ({
                    rowId: item.rowId,
                    userId: item.userId,
                    project: item.project,
                    start_time: item.start_time,
                    end_time: item.end_time,
                    normalHours: item.normalHours,
                    hourlyRate: item.hourlyRate,
                    projectTask: item.projectTask,
                    entryLocalDate: item.entryLocalDate
                }));
                
                // Call API to import timesheet data
                const response = await dispatch(importTimesheet({entries:formattedItems, globalStartTime: globalSettings.startTime, globalEndTime: globalSettings.endTime}));
                
                // Check if there were any import errors
                if (response && response.errors && response.errors.length > 0) {
                    // Show notification
                    dispatch(enqueueSnackbar({
                        message: `Import completed with ${response.successful} successful and ${response.failed} failed entries.`,
                        options: {
                            key: new Date().getTime() + Math.random(),
                            variant: 'warning',
                            autoHideDuration: 5000
                        }
                    }));

                    validateResponse(response);
                    
                } else {
                    // Show success notification
                    dispatch(enqueueSnackbar({
                        message: `Successfully imported ${items.length} timesheet ${items.length === 1 ? 'entry' : 'entries'}.`,
                        options: {
                            key: new Date().getTime() + Math.random(),
                            variant: 'success',
                            autoHideDuration: 5000
                        }
                    }));
                    
                    // Navigate back to timesheet page on success if there were no errors
                    navigate(`/${TIMESHEET}/allocations`);
                }
            } catch (error) {
                console.error('Error importing timesheet data:', error);
                
                // Show error notification
                dispatch(enqueueSnackbar({
                    message: `Error importing timesheet data: ${error.message || 'Unknown error'}`,
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: 'error',
                        autoHideDuration: 5000
                    }
                }));
            }
        } else if (count.valid === 0) {
            // If no valid entries found
            dispatch(enqueueSnackbar({
                message: 'No valid timesheet entries found. Please fix validation errors and try again.',
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                    autoHideDuration: 5000
                }
            }));
        } else {
            // If there are some valid and some invalid entries
            dispatch(enqueueSnackbar({
                message: `${count.invalid} invalid ${count.invalid === 1 ? 'entry' : 'entries'} found. Use "Ignore & Import" to proceed with only valid entries, or fix errors first.`,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'warning',
                    autoHideDuration: 5000
                }
            }));
        }
    };

    return (
        <div className="purchase-order-block">
            <div className="row">
                <div className="w-auto">
                    <h2 className="content-heading text-uppercase">Import Timesheet</h2>
                    <div className="col-12 breadcrumb-block p-0">
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={cancelHandler}>
                                Timesheet
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Import Timesheet</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </div>
                <div className="w-auto ms-auto my-auto">
                    <div className="d-flex gap-3">
                        <button
                            className="secondarybtn btn-ch btn btn-primary"
                            onClick={exportToExcel}>
                            Download Template
                        </button>
                        {/* {data.length > 0 && (
                            <button
                                className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                                onClick={() => validateData()}>
                                Validate
                            </button>
                        )} */}
                                <button
                                    disabled={Number(status?.invalid) > 0 || Number(status?.valid) === 0}
                                    className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                                    onClick={importData}>
                                    Import
                                </button>
                            
                    </div>
                </div>
            </div>

            {globalUser &&
                    <div className="row mt-2 mb-1">
                        <FileUplaoder
                            title={'Upload Timesheet'}
                            files={file ? [file] : []}
                            fileHandler={(files) => {
                            setFile(files[0]);
                            parseXlsx(files[0]);
                            }}
                            acceptedFileTypes={allowedExcelTypes}
                        />
                    </div>}

            {data.length > 0 && (
                <div className="row mt-2">
                    <div className="d-flex justify-content-between mt-2 mb-3">
                        <span className="align-content-center text-decoration-underline text-left fw-medium">
                            Data
                        </span>
                        <div className="d-flex gap-3">
                            <span className="align-content-center text-primary">
                                Total : {status?.total}
                            </span>
                            <span className="align-content-center text-success">
                                Valid : {status?.valid}
                            </span>
                            <span className="align-content-center text-danger d-flex align-items-center">
                                Invalid : {status?.invalid}
                                {status?.invalid > 0 && (
                                    <button 
                                        className="btn btn-link btn-sm p-0 ms-2"
                                        onClick={() => setShowInvalid(!showInvalid)}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        {showInvalid ? '(Show All)' : '(Show Invalid)'}
                                    </button>
                                )}
                            </span>
                        </div>
                    </div>
                    <CustomTable
                        toolbar={false}
                        enableSearch={true}
                        columns={getTimesheetColumns()}
                        data={showInvalid ? data.filter(item => !item.valid) : data}
                        fileName="timesheet"
                        options={{
                            rowStyle: rowData => {
                                return rowData.rowStyle || {};
                            }
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export default ImportTimesheet;